import React from "react";
import { toast } from "react-toastify";
import SectionHeader from "./SectionHeader";
import ScrollReveal from "./ScrollReveal";
import api from "../services/api";

const VoucherSection = ({ vouchers = [], userVouchers = [], onRefresh }) => {
  const activeVouchers = (vouchers || []).filter((v) => {
    const now = new Date();
    const start = v.startDate ? new Date(v.startDate) : new Date(0);
    const end = v.expiryDate ? new Date(v.expiryDate) : new Date(0);
    const isValidType = v.usageType === "promotion";
    return isValidType && v.isActive && now >= start && now <= end;
  });

  if (activeVouchers.length === 0) return null;

  const handleCollect = async (voucherId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.info("Vui lòng đăng nhập để lưu voucher");
      return;
    }

    try {
      await api.post("/vouchers/collect", { voucherId });
      toast.success("Lưu voucher thành công! Hãy kiểm tra trong kho của bạn.");
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err.response?.data || "Không thể lưu voucher");
    }
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("vi-VN").format(val) + "₫";
  };

  return (
    <section className="max-w-7xl mx-auto px-4 py-4 sm:py-16 bg-white dark:bg-slate-950 transition-colors duration-300">
      <ScrollReveal variants="fade-up" threshold={0.1}>
        <SectionHeader
          title="ƯU ĐÃI ĐẶC BIỆT"
          subtitle="Thu thập mã ngay, mua sắm tiết kiệm hơn"
          accentColor="red"
        />
      </ScrollReveal>

      <div className="flex lg:grid overflow-x-auto lg:overflow-x-visible pb-4 lg:pb-0 gap-3 sm:gap-6 mt-3 sm:mt-10 snap-x snap-mandatory scrollbar-hide grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
        {activeVouchers.map((v, i) => {
          const isCollected = userVouchers.some((uv) => uv._id === v._id);
          const isOutOfStock = v.quantity !== undefined && v.quantity <= 0;

            return (
            <ScrollReveal key={v._id} variants="scale-up" delay={i * 0.1} threshold={0.1}>
              <div className={`group relative rounded-2xl p-2.5 sm:p-6 transition-all duration-300 hover:shadow-2xl overflow-hidden min-w-[85%] sm:min-w-0 md:w-auto snap-start border-2 border-dashed ${
                v.type === "gift" 
                ? "bg-white dark:bg-slate-900 border-blue-200 dark:border-blue-900/30 hover:shadow-blue-500/10" 
                : "bg-red-600 border-red-500 hover:shadow-red-500/20"
              } hover:-translate-y-1`}>
                {/* Trang trí hình tròn ở cạnh bên (kiểu vé/voucher) */}
                <div className={`absolute top-1/2 -left-3 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-r-2 border-dashed -translate-y-1/2 z-10 ${
                   v.type === "gift" ? "bg-white dark:bg-slate-950 border-blue-200 dark:border-blue-900/30" : "bg-red-600 border-red-500"
                }`}></div>
                <div className={`absolute top-1/2 -right-3 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-l-2 border-dashed -translate-y-1/2 z-10 ${
                   v.type === "gift" ? "bg-white dark:bg-slate-950 border-blue-200 dark:border-blue-900/30" : "bg-red-600 border-red-500"
                }`}></div>

                <div className="flex items-start justify-between relative z-0">
                  <div className="flex-1 min-w-0">
                    <div className={`inline-block px-2.5 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-black rounded-full mb-2 sm:mb-3 uppercase tracking-wider ${
                      v.type === "gift"
                      ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      : "bg-white text-red-600"
                    }`}>
                      {v.type === "gift" ? "QUÀ TẶNG" : v.type === "percent" ? `Giảm ${v.value}%` : `Giảm ${formatCurrency(v.value)}`}
                    </div>
                    <h3 className={`text-sm sm:text-xl font-black mb-0.5 sm:mb-1 transition-colors truncate ${
                      v.type === "gift" 
                        ? "text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400" 
                        : "text-white"
                    }`}>
                      {v.type === "gift" ? v.giftName : (v.description || "Ưu đãi mua sắm")}
                    </h3>
                    <p className={`text-[9px] sm:text-sm mb-2 sm:mb-4 ${
                      v.type === "gift" ? "text-gray-500 dark:text-slate-400" : "text-red-100"
                    }`}>
                      Đơn từ <span className={`font-bold ${v.type === "gift" ? "text-gray-700 dark:text-slate-200" : "text-white"}`}>{formatCurrency(v.minOrder)}</span>
                    </p>
                    
                    <div className="flex items-center justify-between sm:flex-col sm:items-start gap-1 mb-2 sm:mb-6">
                      <div className={`flex items-center gap-1 text-[9px] ${v.type === "gift" ? "text-gray-400" : "text-red-100"}`}>
                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">Hạn: {new Date(v.expiryDate).toLocaleDateString("vi-VN")}</span>
                      </div>
                      {v.quantity !== undefined && (
                        <div className={`flex items-center gap-1 text-[9px] ${v.type === "gift" ? "text-gray-400" : "text-red-100"}`}>
                          <svg className={`w-2.5 h-2.5 ${v.type === "gift" ? "text-blue-500" : "text-white"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                          </svg>
                          Còn lại: <span className={`font-black ${v.type === "gift" ? "text-blue-600 dark:text-blue-400" : "text-white"}`}>{v.quantity}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={`pt-3 sm:pt-4 border-t ${v.type === "gift" ? "border-gray-100 dark:border-slate-800" : "border-red-500"}`}>
                  <button
                    disabled={isCollected || isOutOfStock}
                    onClick={() => handleCollect(v._id)}
                    className={`w-full py-1.5 sm:py-3 rounded-xl font-black text-[9px] sm:text-xs transition-all duration-300 flex items-center justify-center gap-2 tracking-widest ${
                      isCollected 
                        ? "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 cursor-default" 
                        : isOutOfStock
                        ? "bg-gray-100 dark:bg-slate-800 text-gray-400 cursor-not-allowed"
                        : v.type === "gift"
                        ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200 dark:shadow-none active:scale-95"
                        : "bg-white text-red-600 hover:bg-red-50 shadow-lg shadow-red-900/20 active:scale-95"
                    }`}
                  >
                    {isCollected ? (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        ĐÃ LƯU
                      </>
                    ) : isOutOfStock ? (
                      <>HẾT LƯỢT</>
                    ) : (
                      <>LƯU VOUCHER</>
                    )}
                  </button>
                </div>
              </div>
            </ScrollReveal>
          );
        })}
      </div>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default VoucherSection;
